import { ResponsiveDialog, Button } from '../../components/ui';
import type { BillRun } from '../../api/types';

interface RerunDialogProps {
  open: boolean;
  /**
   * Called whenever Radix wants to dismiss the dialog (Escape, backdrop
   * click, or the header close button) as well as the explicit
   * Cancel/Done button. The in-flight guard (rerunningAll ||
   * rerunningIds.size > 0) must be re-checked here — unlike the old
   * hand-rolled overlay, Radix's Escape/outside-click dismissal does not
   * know about that guard on its own, so without this the new dismiss
   * paths could close the modal mid-flight.
   */
  onRequestClose: () => void;
  rerunTargets: BillRun[];
  outstandingRerunTargets: BillRun[];
  rerunResults: Record<string, { ok: boolean; message?: string }>;
  rerunningIds: Set<string>;
  rerunningAll: boolean;
  rerunReasons: Record<string, string>;
  onReasonChange: (runId: string, value: string) => void;
  onRerunOne: (runId: string) => void;
  onRerunAll: () => void;
  rerunMissingCount: number;
  canRerunAll: boolean;
  formatDate: (value: string) => string;
}

export default function RerunDialog({
  open,
  onRequestClose,
  rerunTargets,
  outstandingRerunTargets,
  rerunResults,
  rerunningIds,
  rerunningAll,
  rerunReasons,
  onReasonChange,
  onRerunOne,
  onRerunAll,
  rerunMissingCount,
  canRerunAll,
  formatDate,
}: RerunDialogProps) {
  const title =
    rerunTargets.length === 0
      ? 'Nothing to Re-Run'
      : `Re-Run ${rerunTargets.length} Bill Run${rerunTargets.length !== 1 ? 's' : ''}`;

  const closeDisabled = rerunningAll || rerunningIds.size > 0;

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && !closeDisabled) onRequestClose();
      }}
      title={title}
      description="Recalculates the selected pending-approval bill runs from current attribute values; each re-run needs its own written justification."
      footer={
        <>
          <div className="mr-auto text-xs text-content-muted">
            {(() => {
              const done = rerunTargets.filter(r => rerunResults[r.id]?.ok).length;
              const failed = rerunTargets.filter(r => { const res = rerunResults[r.id]; return res && !res.ok; }).length;
              return `${done}/${rerunTargets.length} re-queued${failed ? ` · ${failed} failed` : ''}`;
            })()}
          </div>
          <Button variant="secondary" onClick={onRequestClose} disabled={closeDisabled}>
            {Object.values(rerunResults).some(v => v.ok) ? 'Done' : 'Cancel'}
          </Button>
          {outstandingRerunTargets.length > 1 && (
            <Button
              variant="warning"
              onClick={onRerunAll}
              disabled={!canRerunAll}
              title={rerunMissingCount > 0
                ? `Fill in ${rerunMissingCount} more justification${rerunMissingCount === 1 ? '' : 's'} to enable bulk re-run`
                : 'Re-run every outstanding bill in one go'}
              className="whitespace-nowrap"
            >
              {rerunningAll
                ? 'Re-Running…'
                : rerunMissingCount > 0
                  ? `Re-Run All (${rerunMissingCount} missing)`
                  : `Re-Run All (${outstandingRerunTargets.length})`}
            </Button>
          )}
        </>
      }
    >
      {rerunTargets.length > 0 && (
        <p className="text-sm text-content-muted mb-4">
          Existing bills will be cleared and each run recalculated with current attribute values.
          Every re-run needs its own justification.
        </p>
      )}

      {rerunTargets.length === 0 ? (
        <p className="text-sm text-content-muted py-6 text-center">
          None of the selected runs are still in <span className="font-medium">pending approval</span>, so there is nothing to re-run.
        </p>
      ) : (
        <div className="border border-border-token rounded-lg divide-y divide-border-token max-h-96 overflow-y-auto">
          {rerunTargets.map(run => {
            const result = rerunResults[run.id];
            const busy = rerunningIds.has(run.id);
            const reason = rerunReasons[run.id] || '';
            return (
              <div key={run.id} className="px-4 py-3">
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-content truncate">
                      {formatDate(run.period_start)} – {formatDate(run.period_end)}
                    </p>
                    <p className="text-xs text-content-muted truncate">
                      {run.pocket_name || '—'}{run.active_tenant_name ? ` · ${run.active_tenant_name}` : ''}
                      {(run.rerun_count ?? 0) > 0 && (
                        // text-amber-700 alone is 3.02:1 against the dark
                        // card — fails. dark:text-amber-400 (9.09:1, computed
                        // by hand) matches the pair already established for
                        // this exact hue elsewhere (AppShell.tsx,
                        // MeterConfigModal.tsx, BuildingDetail.tsx).
                        <span className="ml-1 text-amber-700 dark:text-amber-400">· re-run {run.rerun_count}×</span>
                      )}
                    </p>
                  </div>
                  {result?.ok ? (
                    // text-green-700 alone is 3.02:1 against the dark card —
                    // fails. dark:text-green-400 (8.72:1, computed by hand)
                    // matches VirtualMeters.tsx's already-documented pair for
                    // this exact shade.
                    <span className="shrink-0 text-xs font-medium text-green-700 dark:text-green-400">✓ Re-queued</span>
                  ) : (
                    <Button
                      variant="warning"
                      size="sm"
                      onClick={() => onRerunOne(run.id)}
                      disabled={busy || !reason.trim() || rerunningAll}
                      className="shrink-0"
                    >
                      {busy ? 'Re-Running…' : 'Re-Run'}
                    </Button>
                  )}
                </div>
                {!result?.ok && (
                  <textarea
                    value={reason}
                    onChange={e => onReasonChange(run.id, e.target.value)}
                    placeholder="Why is this bill run being re-run? (e.g. 'tariff version updated', 'meter assignment corrected')"
                    rows={2}
                    disabled={busy || rerunningAll}
                    className="w-full px-2 py-1 border border-amber-300 rounded text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent disabled:bg-surface-sunken"
                  />
                )}
                {result && !result.ok && (
                  // text-red-600 alone is 3.14:1 against the dark card —
                  // fails. dark:text-red-400 (5.49:1, computed by hand)
                  // matches VirtualMeters.tsx's already-documented pair.
                  <p className="mt-1 text-xs text-red-600 dark:text-red-400">{result.message || 'Re-run failed'}</p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </ResponsiveDialog>
  );
}
